import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ExamIcon, ShareIcon, EyeIcon, UserIcon } from 'vlab-frontend-components';
import { insightsApi } from '../../services/api';
import { metrics } from './constants';
import { NotFoundIcon } from '../../components/icons';
import Card from '../../components/Card';
import Curiosities from '../../components/Curiosities';
import LineChart from '../../components/LineChart';
import FeedbackRatingTabs from '../../components/FeedbackRatingTab';
import RatingItem from '../../components/RatingItem';
import Overview from '../../components/OverviewCard';
import { IRating } from '../../interface/ratingsInterface';
import { Pagination } from '../../components/pagination/pagination';

export default function AppEnabled({
    insightsData,
    selectedOrganization,
    dateRange,
    pagination, 
    setPagination,
    setEnableRequest,
    isLoading
}: any) {
    const { t } = useTranslation()
    const [activeTab, setActiveTab] = useState<'insights' | 'reviews'>('insights');
    const [ratesData, setRatesData] = useState<any>(null);
    const [ratesLoading, setRatesLoading] = useState<boolean>(false);

    useEffect(() => {
        async function fetchRates() {
            if (
                activeTab === 'reviews'
            ) {
                try {
                    setRatesLoading(true);
                    const formattedStartDate = format(dateRange.startDate, 'yyyy-MM-dd');
                    const formattedEndDate = format(dateRange.endDate, 'yyyy-MM-dd');
                    const response = await insightsApi.getRatesByOrganization(
                        selectedOrganization,
                        formattedStartDate,
                        formattedEndDate
                    );
                    setRatesData(response);
                } catch (error) {
                    console.error('Erro ao buscar dados de avaliações:', error);
                } finally {
                    setRatesLoading(false);
                }
            }
        }

        fetchRates();
    }, [activeTab, selectedOrganization, dateRange.startDate, dateRange?.endDate]);

    const lineSeriesData = insightsData?.insight_list
        ? metrics.map((metric) => ({
              name: t(`appEnabled.${metric}`),
              data: insightsData.insight_list.map((item: any) => ({
                  x: new Date(item.insight_date).getTime(),
                  y: item[metric]
              }))
          }))
        : []

    if (!insightsData) return null

    const handleContentTypeChange = (contentType: string) => {
        setPagination({
            ...pagination,
            currentPage: 0,
            detailsLevel: contentType
        });
        
        setEnableRequest({
            selectedDate: false,
            onlyOrg: true
        });
    }

    return (
        <div className="py-8 leading-3 text-[#51525F]">
            <div className="flex flex-col w-full">

                <div className="flex border-b border-[#DDE5EC] mb-8">
                    <button
                        className={`pb-2 font-semibold text-[18px] leading-[18px] mr-6 relative ${
                            activeTab === 'insights' ? 'text-[#0166FF]' : 'text-[#B7C6D1]'
                        }`}
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                        onClick={() => setActiveTab('insights')}
                    >
                        {t('appEnabled.insights')}
                        {activeTab === 'insights' && (
                            <div className="absolute h-[3px] bg-[#0166FF] rounded-[30px] bottom-[-1.5px] left-0 w-full" />
                        )}
                    </button>
                    <button
                        className={`pb-2 font-semibold text-[18px] leading-[18px] relative ${
                            activeTab === 'reviews' ? 'text-[#0166FF]' : 'text-[#B7C6D1]'
                        }`}
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                        onClick={() => {
                            setActiveTab('reviews');
                            setPagination({ ...pagination, currentPage: 0, detailsLevel: 'ratings', pageSize: 10 });
                        }}
                    >
                        {t('appEnabled.reviews')}
                        {activeTab === 'reviews' && (
                            <div className="absolute h-[3px] bg-[#0166FF] rounded-[30px] bottom-[-1.5px] left-0 w-full" />
                        )}
                    </button>
                </div>

                {activeTab === 'insights' ? (
                    <div className="flex flex-col lg:flex-row gap-6 w-full h-fit ">
                        <div className="h-full ">
                            <div className="flex gap-6 w-full lg:w-[calc(100vw-500px)]  overflow-x-auto py-2 lg:py-0 mb-4">
                                <Card
                                    fullWidth
                                    text={t('appEnabled.total_exams')}
                                    number={insightsData?.insight?.total_exams || 0}
                                    compare={insightsData?.compare?.total_exams || 0}
                                    icon={<ExamIcon width="21px" height="21px" color="white" />}
                                    bgColor="bg-[#0166FF]"
                                    borderColor="border-[#0166FF]"
                                />
                                <Card
                                    fullWidth
                                    text={t('appEnabled.logo_share')}
                                    number={insightsData?.insight?.logo_share || 0}
                                    icon={<ShareIcon width="21px" height="21px" color="white" />}
                                    bgColor="bg-[#5D4DEB]"
                                    borderColor="border-[#5D4DEB]"
                                    compare={insightsData?.compare?.logo_share || 0}
                                />
                                <Card
                                    fullWidth
                                    text={t('appEnabled.exam_view_quantity')}
                                    number={insightsData?.insight?.exam_view_quantity || 0}
                                    icon={<EyeIcon width="21px" height="21px" color="white" />}
                                    bgColor="bg-[#E20101]"
                                    borderColor="border-[#E20101]"
                                    compare={insightsData?.compare?.exam_view_quantity || 0}
                                />
                                <Card
                                    fullWidth
                                    text={t('appEnabled.baby_users_quantity')}
                                    number={insightsData?.insight?.baby_users_quantity || 0}
                                    icon={<UserIcon width="17px" height="17px" color="white" />}
                                    bgColor="bg-[#FEC551]"
                                    borderColor="border-[#FEC551]"
                                    compare={insightsData?.compare?.baby_users_quantity || 0}
                                />
                                <Card
                                    fullWidth
                                    text={t('appEnabled.booking_clicks')}
                                    number={insightsData?.insight?.booking_clicks || 0}
                                    icon={<UserIcon width="17px" height="17px" color="white" />}
                                    bgColor="bg-[#48E1C6]"
                                    borderColor="border-[#48E1C6]"
                                    compare={insightsData?.compare?.booking_clicks || 0}
                                />
                            </div>
                            <div className="flex gap-4 mb-4">
                                <div className=" bg-[#F7F8FC] w-full max-w-[100%]  h-[100%] flex items-center justify-center flex-col rounded ">
                                    {lineSeriesData.length > 0 ? (
                                        <LineChart data={insightsData?.insight_list} />
                                    ) : (
                                        <div className="flex flex-col items-center my-4">
                                            <NotFoundIcon />
                                            <p className="mt-4 text-[12px] font-semibold text-[#51525F]">{t('appEnabled.insufficient_data')}</p>
                                            <p className="mt-2 text-[10px] font-semibold text-[#8B98A3]">{t('appEnabled.clear_filters_try_again')}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-col h-[100%]">
                                <FeedbackRatingTabs 
                                    feedbacks={insightsData.feedbacks} 
                                    ratings={insightsData.ratings} 
                                    ratingsAverage={insightsData.ratingsAverage}
                                    pagination={pagination}
                                    setPagination={setPagination}
                                    activeTab={pagination.detailsLevel || 'ratings'}
                                    onContentTypeChange={handleContentTypeChange}
                                    isLoading={isLoading}
                                />
                            </div>
                        </div>
                        <div className="w-[338px] ">
                            <Curiosities curiosities={insightsData.insight} />
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col w-full">
                        {insightsData.ratings && insightsData.ratings.length > 0 ? (
                            <div className="flex h-full relative gap-6">      
                                <div className="w-full w-4/5 pr-4">
                                    <div className="flex flex-col h-[calc(100vh-264px)]">
                                        <div
                                            className="flex flex-col gap-4 overflow-y-auto pr-4 flex-grow"
                                            style={{ minHeight: '400px' }}
                                        >
                                            {insightsData.ratings.map((rating: IRating, index: number) => (
                                                <RatingItem 
                                                    key={index} 
                                                    rating={rating} 
                                                    haveIcon={true}
                                                    haveShare={true}
                                                    organizationCode={selectedOrganization}
                                                    backgroundColor="bg-[#F7F8FC]"
                                                    ratingStarColor='text-[#51525F]'
                                                />
                                            ))}
                                        </div>
                                        <div className="mt-6">
                                            <Pagination
                                                currentPage={pagination.currentPage + 1}
                                                totalPages={pagination.totalPages}
                                                pageSize={pagination.pageSize}
                                                total={pagination.total}
                                                onPageChange={(page) => setPagination({ ...pagination, currentPage: page - 1 })}
                                            />
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="block w-1/5 h-full sticky top-0">
                                    {ratesLoading ? (
                                        <div className="animate-pulse h-96 bg-gray-200 rounded"></div>
                                    ) : ratesData ? (
                                        <Overview
                                            total={ratesData.total}
                                            total_comments={ratesData.total_comments}
                                            average={ratesData.average}
                                            quantity1={ratesData.quantity1}
                                            quantity2={ratesData.quantity2}
                                            quantity3={ratesData.quantity3}
                                            quantity4={ratesData.quantity4}
                                            quantity5={ratesData.quantity5}
                                        />
                                    ) : null}
                                </div>
                            </div>
                        ) : (
                            <div className="w-full flex items-center justify-center py-12">
                                <span className="text-xl font-medium text-[#51525F]">
                                    {t('appEnabled.noRatingsFound')}
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}